import { Controller } from '@hotwired/stimulus';

/**
 * This controller opens and closes Google Maps info windows.
 *
 * It expects no, one, or multiple preview targets. Each preview target is expected to have a
 * data-id and a data-title attribute with values {id} and {title}.
 *
 * Adding and removing targets triggers the update function, which does the following:
 *
 * - If there is no preview target, a possibly open info window is closed.
 * - If there is exactly one preview target, an info window is opened at the marker, whose
 *   project-ids value contains " {id} ". Its content is set to {title}.
 * - If there is more than one preview target, an info window is opened at the marker, whose
 *   project-ids value contains " {id} ", where {id} belongs to the first preview target (assuming
 *   that multiple preview targets always belong to the same marker). The info window content is set
 *   to {multipleProjectsMarkerValue}.
 */

// Connects to data-controller="gmaps-infowindow"
export default class extends Controller {
  static targets = ['preview', 'map'];
  static values = {
    multipleProjectsMarker: String,
  };

  previewTargetConnected() {
    this.update();
  }

  previewTargetDisconnected() {
    this.update();
  }

  /**
   * update sets the info window according to the number and content of preview targets.
   */
  async update() {
    // Create an info window object if there is none yet.
    await this.setInfoWindow();

    // If there are preview targets, they were created by clicking a marker. Find any project ID of
    // these previewed projects (to find the corresponding marker and thus position the info window)
    // and get the content for the info window (which is either the project's title or a predefined
    // message like "here are multiple projects").
    const [projectId, infoWindowContent] = this.preview();

    // If there are no preview targets, we want to close the possibly open info window.
    if (projectId == undefined) {
      this.infoWindow.close();
      return;
    }

    this.infoWindow.setContent(infoWindowContent);

    // Find the marker that corresponds to the previewed projects.
    const matchingNode = document.querySelector(
      `[project-ids*=" ${projectId} "`,
    );

    // Position and then open the info window at the found marker.
    if (matchingNode) {
      const marker = matchingNode.closest('gmp-advanced-marker');

      if (marker && marker.position) {
        // Open the info window at marker.

        // The open function as an anchor option (infoWindow.open({ anchor: ... })) but we can't use
        // use it because it is somewhat buggy, especially when you have an open infoWindow at a
        // cluster marker and then click on this marker (or the other way around). So we set the
        // position manually.
        this.infoWindow.setPosition(marker.position);

        // The marker itself has a certain height. We want to position the info window just above
        // the marker.
        this.infoWindow.setOptions({
          pixelOffset: new google.maps.Size(0, -marker.clientHeight),
        });

        this.infoWindow.open(this.mapTarget.innerMap);
      }
    }
  }

  /**
   * setInfoWindow creates an info window object - if there is none yet - and stores it in
   * this.infoWindow.
   */
  async setInfoWindow() {
    if (this.infoWindow === undefined) {
      this.infoWindow = true;

      await new Promise((resolve) => {
        if (window.google && window.google.maps) {
          resolve();
        }
      });

      this.infoWindow = new google.maps.InfoWindow();

      // Close the preview when the user clicks on the close button of the info window.
      google.maps.event.addListener(
        this.infoWindow,
        'closeclick',
        this.closePreview,
      );

      // Close the preview when the user clicks on the map outside of a possibly open info window.
      google.maps.event.addListener(
        this.mapTarget.innerMap,
        'click',
        this.closePreview,
      );
    }
  }

  /**
   * closePreview removes all preview targets, which also closes the infoWindow (see
   * previewTargetDisconnected).
   */
  closePreview() {
    document.querySelector('.preview-container').replaceChildren();
  }

  /**
   * Returns an array like [{id}, {content}].
   *
   * If there is one preview target, {id} is the ID of this project and {content} is its title.
   * If there are multiple preview targets, {id} is the ID of the first project and {content} is a
   * given string that expresses that at this point are multiple projects.
   *
   * So {id} can be used to find the corresponding marker and thus position the info window and
   * {content} will be its content.
   */
  preview() {
    if (!this.hasPreviewTarget) {
      return [];
    } else if (this.previewTargets.length == 1) {
      return [
        this.previewTarget.getAttribute('data-id'),
        this.previewTarget.getAttribute('data-title'),
      ];
    } else {
      return [
        this.previewTargets[0].getAttribute('data-id'),
        this.multipleProjectsMarkerValue,
      ];
    }
  }
}
